import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Empty-state for an admin viewing a sheet (or day) with no events. Mirrors
 * the iOS `AdminEmptyAgenda`: gives the admin three obvious starting points
 * instead of a dead-end "No events scheduled" line.
 *
 * Props:
 *   - artistId:           used to pre-select the artist when jumping to the builder
 *   - date:               yyyy-MM-dd — also passed to the builder
 *   - hideCalendarTile:   true when the artist already has Google Calendar
 *                         wired up (skips the redundant Connect prompt)
 */
export default function AdminTodayEmpty({ artistId, date, hideCalendarTile = false }) {
  const navigate = useNavigate();

  const newSheetURL = (() => {
    const qs = new URLSearchParams();
    if (artistId) qs.set('artist', artistId);
    if (date) qs.set('date', date);
    const s = qs.toString();
    return s ? `/admin/sheets/new?${s}` : '/admin/sheets/new';
  })();

  return (
    <div style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', padding:'24px 20px' }}>
      <div style={{ textAlign:'center', marginBottom:18 }}>
        <p style={{ fontSize:15, fontWeight:600, color:'var(--text)', margin:'0 0 4px' }}>Nothing scheduled yet</p>
        <p style={{ fontSize:13, color:'var(--text2)', margin:0 }}>Pick how you want to start this day.</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: hideCalendarTile ? '1fr 1fr' : '1fr 1fr 1fr', gap:10 }}>
        {!hideCalendarTile && (
          <Tile
            icon="fa-calendar-plus"
            tint="#4285f4"
            title="Connect Google Calendar"
            sub="Pull events automatically"
            onClick={() => navigate('/admin/settings')}
          />
        )}
        <Tile
          icon="fa-file-import"
          tint="#a855f7"
          title="Import a tour PDF"
          sub="Extract events with AI"
          onClick={() => navigate(newSheetURL)}
        />
        <Tile
          icon="fa-pen-to-square"
          tint="#10b981"
          title="Build a sheet"
          sub="Add events by hand"
          onClick={() => navigate(newSheetURL)}
        />
      </div>
    </div>
  );
}

function Tile({ icon, tint, title, sub, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', gap:8,
        padding:'18px 12px', borderRadius:12,
        background:'var(--bg-soft, rgba(255,255,255,0.02))',
        border:'1px solid var(--border)',
        cursor:'pointer', textAlign:'center',
        fontFamily:'inherit',
        transition:'transform 0.1s, border-color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = tint; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
      <div style={{
        width:38, height:38, borderRadius:10,
        background:`${tint}22`, color:tint,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <i className={`fa-solid ${icon}`} style={{ fontSize:16 }} />
      </div>
      <div>
        <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:'0 0 2px' }}>{title}</p>
        <p style={{ fontSize:11, color:'var(--text2)', margin:0, lineHeight:1.3 }}>{sub}</p>
      </div>
    </button>
  );
}
